import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  Upload, Modal, message, Space, Typography, Tooltip, Progress, Popconfirm, Image, Spin,
  Button, Popover, theme,
} from 'antd';
import {
  InboxOutlined, FilePdfOutlined, FileImageOutlined, EyeOutlined, DeleteOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

const { Dragger } = Upload;
const { Text } = Typography;

const PORTAL_BUCKET = 'openep-portal';

const safeParseFileIds = (value) => {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** MinIO object uploader (PromedHIS FileUploader pattern; presigned GET URLs). */
const ObjectStorageUploader = ({
  value = '[]',
  onChange,
  category = 'uploads',
  ownerId = '',
  ownerName = '',
  bucketName = PORTAL_BUCKET,
  readonly = false,
}) => {
  const { token } = theme.useToken();
  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const ownChangeRef = useRef(false);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  useEffect(() => {
    if (ownChangeRef.current) {
      ownChangeRef.current = false;
      return;
    }
    const fileIds = safeParseFileIds(value);
    if (fileIds.length === 0) {
      setFileList([]);
      return;
    }
    fetchFileDetails(fileIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, bucketName]);

  const fetchFileDetails = async (fileIds) => {
    if (!userInfo?.token) return;
    try {
      const response = await axios.post(
        `/dotnet_api/v1/storage/objects/${bucketName}`,
        fileIds,
        { headers: { Authorization: `Bearer ${userInfo.token}` } },
      );
      const uniqueFiles = response.data.reduce((acc, file) => {
        if (!acc.find((f) => f.id === file.id)) {
          acc.push({
            uid: file.id,
            name: file.name,
            status: 'done',
            url: file.url,
            type: file.contentType,
            size: file.size,
            ...file,
          });
        }
        return acc;
      }, []);
      setFileList(uniqueFiles);
    } catch {
      message.error('Failed to load files');
    }
  };

  const emitIds = (ids) => {
    ownChangeRef.current = true;
    onChange(JSON.stringify(ids));
  };

  const customRequest = async ({ file, onSuccess, onError, onProgress }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    formData.append('ownerId', ownerId);
    formData.append('ownerName', ownerName);
    formData.append('bucketName', bucketName);
    try {
      const response = await axios.post('/dotnet_api/v1/storage/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${userInfo.token}`,
        },
        onUploadProgress: (progressEvent) => {
          const percent = (progressEvent.loaded / progressEvent.total) * 100;
          onProgress({ percent });
        },
      });
      onSuccess(response.data, file);
    } catch (error) {
      onError(error);
    }
  };

  const handleUpload = (info) => {
    const { status, response, name } = info.file;
    if (status === 'uploading') {
      setFileList((prev) => {
        const updated = [...prev];
        const idx = updated.findIndex((item) => item.uid === info.file.uid);
        if (idx > -1) updated[idx] = { ...updated[idx], percent: info.file.percent };
        else updated.push({ ...info.file });
        return updated;
      });
    }
    if (status === 'done') {
      setFileList((prev) => [
        ...prev.filter((file) => file.uid !== info.file.uid),
        {
          uid: response.id,
          name: response.name,
          status: 'done',
          url: response.url,
          type: response.contentType,
          size: response.size,
          ...response,
        },
      ]);
      const current = safeParseFileIds(value);
      emitIds([...current, response.id]);
      message.success(`${name} uploaded`);
    } else if (status === 'error') {
      message.error(`${name} failed to upload`);
    }
  };

  const handleRemove = (file) => {
    setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    emitIds(safeParseFileIds(value).filter((id) => id !== file.id && id !== file.uid));
    if (file.id && userInfo?.token) {
      axios.delete('/dotnet_api/v1/storage/file', {
        params: { bucketName, fileName: file.id },
        headers: { Authorization: `Bearer ${userInfo.token}` },
      }).catch(() => {});
    }
  };

  const handlePreview = (file) => {
    if (file.type === 'application/pdf') {
      setPreviewFile(file);
      setPreviewVisible(true);
    } else if (file.type?.startsWith('image/')) {
      setImagePreviewUrl(file.url);
      setImagePreviewVisible(true);
    } else {
      window.open(file.url, '_blank');
    }
  };

  return (
    <div
      style={{
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        overflow: 'hidden',
      }}
    >
      {!readonly && (
        <Dragger
          customRequest={customRequest}
          showUploadList={false}
          onChange={handleUpload}
          fileList={fileList}
          accept=".pdf,.jpg,.jpeg,.png,.docx"
          multiple
          maxCount={10}
        >
          <Space>
            <InboxOutlined style={{ fontSize: 22, color: token.colorPrimary }} />
            <div style={{ textAlign: 'left' }}>
              <Text strong>Click or drag files to upload</Text>
              <br />
              <Text type="secondary">PDF, DOCX, images. Stored privately in object storage.</Text>
            </div>
          </Space>
        </Dragger>
      )}
      {fileList.map((file) => (
        <div
          key={file.uid}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderTop: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          {file.type?.startsWith('image/') ? <FileImageOutlined /> : <FilePdfOutlined />}
          <Text ellipsis style={{ flex: 1 }}>{file.name}</Text>
          {file.status === 'uploading' ? (
            <Space>
              <Progress percent={Number(file.percent || 0)} size="small" style={{ width: 72 }} />
              <Spin size="small" />
            </Space>
          ) : (
            <Space>
              <Popover
                content={(
                  <div style={{ fontSize: 12 }}>
                    <div>{file.type}</div>
                    <div>{file.size} bytes</div>
                  </div>
                )}
              >
                <Button type="text" size="small" icon={<InfoCircleOutlined />} />
              </Popover>
              <Tooltip title="Preview">
                <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => handlePreview(file)} />
              </Tooltip>
              {!readonly && (
                <Popconfirm title="Delete this file?" onConfirm={() => handleRemove(file)}>
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            </Space>
          )}
        </div>
      ))}
      <Modal open={previewVisible} title={previewFile?.name} footer={null} onCancel={() => setPreviewVisible(false)} width="70%">
        {previewFile?.type === 'application/pdf' && (
          <iframe src={previewFile.url} title="PDF" style={{ width: '100%', height: 500, border: 0 }} />
        )}
      </Modal>
      <Image
        style={{ display: 'none' }}
        src={imagePreviewUrl}
        preview={{
          visible: imagePreviewVisible,
          onVisibleChange: setImagePreviewVisible,
        }}
      />
    </div>
  );
};

export default ObjectStorageUploader;
export { PORTAL_BUCKET };
